import React from 'react';
import styles from './GlobalMapSearch.module.css';

/** Escape regex metacharacters so user input can be embedded safely. */
function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Build a single case-insensitive regex that matches any whitespace-
 * separated token in `query`. Tokens are sorted longest-first so a
 * multi-word phrase like "John Smith" highlights as one chunk when
 * present (instead of two adjacent marks for "John" and "Smith").
 * Returns `null` when the query is empty/whitespace-only — callers
 * treat that as "render plain text".
 *
 * Shared by the regular dropdown's result rows and the slash-command
 * palette's scoped-result rows so users get the same "what matched"
 * affordance regardless of which surface they searched from.
 */
export function buildHighlightRegex(query: string): RegExp | null {
  const tokens = Array.from(
    new Set(
      query
        .split(/\s+/)
        .map(t => t.trim())
        .filter(t => t.length > 0),
    ),
  ).sort((a, b) => b.length - a.length);
  if (tokens.length === 0) return null;
  return new RegExp(`(${tokens.map(escapeRegex).join('|')})`, 'gi');
}

/**
 * Render `text` with every regex-matching substring wrapped in a
 * styled `<mark>` so users can see exactly which characters their
 * query hit. Falls back to plain text when `regex` is null or the
 * text is empty.
 */
export const HighlightedText: React.FC<{
  text: string;
  regex: RegExp | null;
}> = ({ text, regex }) => {
  if (!regex || !text) return <>{text}</>;
  // Reset lastIndex so a `g`-flagged regex used across renders is safe.
  regex.lastIndex = 0;
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        // Odd indices are the captured matches.
        i % 2 === 1 ? (
          // eslint-disable-next-line react/no-array-index-key
          <mark key={i} className={styles.highlight}>
            {part}
          </mark>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </>
  );
};
